import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Avatar,
  Tooltip,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Badge,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  School as SchoolIcon,
  SupervisorAccount as AdminIcon,
  Assessment as StatisticsIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  Message as MessageIcon,
  Schedule as ScheduleIcon,
  ExitToApp as LogoutIcon,
  Notifications as NotificationsIcon,
  CalendarMonth as CalendarIcon,
  People as PeopleIcon,
  CalendarToday as CalendarTodayIcon,
  AccessTime as AccessTimeIcon,
  SchoolOutlined,
} from '@mui/icons-material';
import {
  collection, query, where, orderBy, onSnapshot, doc, updateDoc, getDoc,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userRole, userData, logout } = useAuth();

  const [drawerOpen, setDrawerOpen]             = useState(false);
  const [anchorElUser, setAnchorElUser]         = useState(null);
  const [notifAnchorEl, setNotifAnchorEl]       = useState(null);
  const [notifications, setNotifications]       = useState([]);
  const [unreadCount, setUnreadCount]           = useState(0);
  const [establishmentInfo, setEstablishmentInfo] = useState(null);
  const [canCreatePermanences, setCanCreate]    = useState(false);

  /* ── Établissement ── */
  useEffect(() => {
    if (!userData?.establishmentId) return;
    getDoc(doc(db, 'establishments', userData.establishmentId))
      .then(d => { if (d.exists()) setEstablishmentInfo(d.data()); })
      .catch(console.error);
  }, [userData]);

  /* ── Notifications temps réel ── */
  useEffect(() => {
    if (!userData?.establishmentId || !currentUser?.uid) return;
    const q = query(
      collection(db, 'messages'),
      where('establishmentId', '==', userData.establishmentId),
      where('recipientId', '==', currentUser.uid),
      where('read', '==', false),
      orderBy('createdAt', 'desc'),
    );
    return onSnapshot(q, snap => {
      const list = snap.docs.map(d => {
        const data = d.data();
        let createdAt = new Date();
        if (data.createdAt?.toDate) createdAt = data.createdAt.toDate();
        else if (data.createdAt instanceof Date) createdAt = data.createdAt;
        else if (data.createdAt) {
          const ts = Number(data.createdAt);
          createdAt = new Date(ts > 1e10 ? ts : ts * 1000);
        }
        return { id: d.id, ...data, createdAt };
      });
      setNotifications(list);
      setUnreadCount(list.length);
    }, console.error);
  }, [currentUser, userData]);

  /* ── Permissions permanences ── */
  useEffect(() => {
    if (!currentUser || userRole !== 'chefService') return;
    getDoc(doc(db, 'users', currentUser.uid))
      .then(d => { if (d.exists()) setCanCreate(d.data().canCreatePermanences || false); })
      .catch(console.error);
  }, [currentUser, userRole]);

  /* ── Handlers ── */
  const handleLogout = async () => {
    try { await logout(); navigate('/login'); } catch (e) { console.error(e); }
  };

  const handleNotifClick = async (notif) => {
    try { await updateDoc(doc(db, 'messages', notif.id), { read: true }); } catch (e) { console.error(e); }
    setNotifAnchorEl(null);
    if (!notif.senderId) return;
    const routes = { admin: 'admin', directionRH: 'admin', chefService: 'chef-service', personnel: 'personnel' };
    const base = routes[userData?.role];
    if (base) navigate(`/${base}/messages?contact=${notif.senderId}`);
  };

  const roleLabel = () => ({
    superAdmin: 'Super Admin', admin: 'Admin', directionRH: 'Direction RH',
    chefService: 'Chef de Service', personnel: 'Personnel', utilisateur: 'Utilisateur',
  }[userRole] || '');

  /* ── Liens de navigation par rôle ── */
  const NAV = {
    superAdmin: [],   // superAdmin utilise le drawer uniquement
    admin: [
      { label: 'Tableau de bord',  path: '/admin/dashboard',    icon: <DashboardIcon fontSize="small" /> },
      { label: 'Personnel',        path: '/admin/personnel',     icon: <PeopleIcon fontSize="small" /> },
      { label: 'Calendrier',       path: '/admin/calendar',      icon: <CalendarTodayIcon fontSize="small" /> },
      { label: 'Permanences',      path: '/admin/permanences',   icon: <AccessTimeIcon fontSize="small" /> },
    ],
    directionRH: [
      { label: 'Tableau de bord',  path: '/admin/dashboard',    icon: <DashboardIcon fontSize="small" /> },
      { label: 'Personnel',        path: '/admin/personnel',     icon: <PeopleIcon fontSize="small" /> },
      { label: 'Calendrier',       path: '/admin/calendar',      icon: <CalendarTodayIcon fontSize="small" /> },
      { label: 'Permanences',      path: '/admin/permanences',   icon: <AccessTimeIcon fontSize="small" /> },
    ],
    personnel: [
      { label: 'Emploi du temps',  path: '/personnel/timetable',    icon: <ScheduleIcon fontSize="small" /> },
      { label: 'Permanences',      path: '/personnel/permanences',  icon: <AccessTimeIcon fontSize="small" /> },
      { label: 'Demandes',         path: '/personnel/leave-request',icon: <MessageIcon fontSize="small" /> },
      { label: 'Messages',         path: '/personnel/messages',     icon: <MessageIcon fontSize="small" /> },
    ],
  };

  const chefLinks = [
    { label: 'Personnel',        path: '/chef-service/personnel',   icon: <PersonIcon fontSize="small" /> },
    { label: 'Absences',         path: '/chef-service/absences',    icon: <AccessTimeIcon fontSize="small" /> },
    { label: 'Emploi du temps',  path: '/chef-service/timetable',   icon: <ScheduleIcon fontSize="small" /> },
    { label: 'Calendrier',       path: '/chef-service/calendar',    icon: <CalendarIcon fontSize="small" /> },
    ...(canCreatePermanences
      ? [{ label: 'Permanences', path: '/chef-service/permanences', icon: <AccessTimeIcon fontSize="small" /> }]
      : []),
    { label: 'Messagerie',       path: '/chef-service/messages',    icon: <MessageIcon fontSize="small" /> },
  ];

  const navLinks = userRole === 'chefService' ? chefLinks : (NAV[userRole] || []);

  /* ── Drawer items (superAdmin) ── */
  const drawerItems = [
    { text: 'Tableau de Bord',  icon: <DashboardIcon />, path: '/super-admin/dashboard' },
    { text: 'Établissements',   icon: <SchoolIcon />,    path: '/super-admin/establishments' },
    { text: 'Administrateurs',  icon: <AdminIcon />,     path: '/super-admin/admins' },
    { text: 'Statistiques',     icon: <StatisticsIcon />,path: '/super-admin/statistics' },
    { text: 'Paramètres',       icon: <SettingsIcon />,  path: '/super-admin/settings' },
  ];

  const active = (path) => location.pathname === path;

  /* ────────────────────────────────────────────────────────── */
  return (
    <Box>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: 'linear-gradient(90deg, #093340 0%, #0f4c5c 55%, #1a6b80 100%)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Toolbar variant="dense" sx={{ minHeight: 56, px: 2 }}>

          {/* ── Hamburger (superAdmin uniquement) ── */}
          {userRole === 'superAdmin' && (
            <IconButton
              size="small"
              color="inherit"
              onClick={() => setDrawerOpen(true)}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* ── Logo ── */}
          <Box
            onClick={() => navigate(userRole === 'superAdmin' ? '/super-admin/dashboard' : '#')}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2, cursor: 'pointer', flexShrink: 0 }}
          >
            <Box sx={{
              width: 28, height: 28, borderRadius: '7px',
              background: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <SchoolOutlined sx={{ color: '#1a1a1a', fontSize: 16 }} />
            </Box>
            <Typography sx={{
              fontFamily: '"Outfit", sans-serif', fontWeight: 700,
              fontSize: '0.95rem', color: '#fff', letterSpacing: '-0.01em',
            }}>
              EduGestion
            </Typography>
          </Box>

          {/* ── Liens de navigation ── */}
          {navLinks.length > 0 && (
            <>
              {/* séparateur */}
              <Box sx={{ width: '1px', height: 20, background: 'rgba(255,255,255,0.25)', mr: 1.5, flexShrink: 0 }} />

              {/* liens — rendu direct, sans conteneur overflow */}
              {navLinks.map((item) => (
                <Box
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1.25,
                    py: 0.6,
                    mx: 0.25,
                    borderRadius: '7px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    fontSize: '0.82rem',
                    fontWeight: active(item.path) ? 600 : 400,
                    color: active(item.path) ? '#ffffff' : 'rgba(255,255,255,0.8)',
                    background: active(item.path) ? 'rgba(255,255,255,0.15)' : 'transparent',
                    borderBottom: active(item.path)
                      ? '2px solid #f59e0b'
                      : '2px solid transparent',
                    transition: 'all 0.15s',
                    flexShrink: 0,
                    '&:hover': {
                      color: '#fff',
                      background: 'rgba(255,255,255,0.1)',
                    },
                  }}
                >
                  {item.icon}
                  <span style={{ marginLeft: 2 }}>{item.label}</span>
                </Box>
              ))}
            </>
          )}

          {/* ── Espace flexible ── */}
          <Box sx={{ flexGrow: 1 }} />

          {/* ── Cloche notifications ── */}
          <Tooltip title="Notifications">
            <IconButton
              size="small"
              color="inherit"
              onClick={(e) => setNotifAnchorEl(e.currentTarget)}
              sx={{ mr: 0.5 }}
            >
              <Badge badgeContent={unreadCount} color="error">
                <NotificationsIcon sx={{ fontSize: 20 }} />
              </Badge>
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={notifAnchorEl}
            open={Boolean(notifAnchorEl)}
            onClose={() => setNotifAnchorEl(null)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{ sx: { mt: 1, width: 310, maxHeight: 380, borderRadius: '12px', boxShadow: '0 16px 48px rgba(15,76,92,0.18)' } }}
          >
            <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid #eee' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f4c5c' }}>
                Notifications {unreadCount > 0 && `(${unreadCount})`}
              </Typography>
            </Box>
            {notifications.length > 0
              ? notifications.map(n => (
                <MenuItem key={n.id} onClick={() => handleNotifClick(n)}
                  sx={{ whiteSpace: 'normal', borderBottom: '1px solid #f5f5f5' }}>
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{n.senderName}</Typography>
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.78rem' }}>{n.content}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {n.createdAt instanceof Date ? format(n.createdAt, 'dd/MM/yyyy HH:mm', { locale: fr }) : ''}
                    </Typography>
                  </Box>
                </MenuItem>
              ))
              : (
                <Box sx={{ py: 2.5, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem' }}>
                    Aucune nouvelle notification
                  </Typography>
                </Box>
              )
            }
          </Menu>

          {/* ── Avatar / menu compte ── */}
          <Tooltip title="Mon compte">
            <IconButton
              size="small"
              onClick={(e) => setAnchorElUser(e.currentTarget)}
              sx={{ p: 0.4 }}
            >
              <Avatar sx={{
                width: 30, height: 30, borderRadius: '7px',
                background: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
                color: '#1a1a1a', fontSize: '0.72rem', fontWeight: 700,
              }}>
                {userData?.firstName?.charAt(0)}{userData?.lastName?.charAt(0)}
              </Avatar>
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={anchorElUser}
            open={Boolean(anchorElUser)}
            onClose={() => setAnchorElUser(null)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{ sx: { mt: 1, minWidth: 210, borderRadius: '12px', boxShadow: '0 16px 48px rgba(15,76,92,0.18)' } }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #eee' }}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#1a2332' }}>
                {userData?.firstName} {userData?.lastName}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
                <Chip
                  label={roleLabel()}
                  size="small"
                  sx={{ height: 19, fontSize: '0.67rem', fontWeight: 600, background: 'rgba(15,76,92,0.1)', color: '#0f4c5c' }}
                />
                {userData?.department && (
                  <Typography sx={{ fontSize: '0.72rem', color: '#4a5568' }}>{userData.department}</Typography>
                )}
              </Box>
              {(userRole === 'admin' || userRole === 'directionRH') && establishmentInfo?.anneeScolaire && (
                <Typography sx={{ fontSize: '0.7rem', color: '#4a5568', mt: 0.25 }}>
                  Année {establishmentInfo.anneeScolaire}
                </Typography>
              )}
            </Box>
            <MenuItem
              onClick={() => { setAnchorElUser(null); navigate(`/${userRole === 'utilisateur' ? 'user' : 'personnel'}/profile`); }}
              sx={{ gap: 1.5, px: 2, py: 1 }}
            >
              <PersonIcon sx={{ fontSize: 17, color: '#0f4c5c' }} />
              <Typography sx={{ fontSize: '0.85rem' }}>Mon profil</Typography>
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => { setAnchorElUser(null); handleLogout(); }}
              sx={{ gap: 1.5, px: 2, py: 1 }}
            >
              <LogoutIcon sx={{ fontSize: 17, color: '#ef4444' }} />
              <Typography sx={{ fontSize: '0.85rem', color: '#ef4444' }}>Déconnexion</Typography>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* ── Drawer superAdmin ── */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 250 } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{
            p: 2, background: 'linear-gradient(135deg, #093340, #0f4c5c)',
            display: 'flex', alignItems: 'center', gap: 1.5,
          }}>
            <Box sx={{ width: 32, height: 32, borderRadius: '8px', background: 'linear-gradient(135deg, #f59e0b, #fbbf24)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <SchoolOutlined sx={{ color: '#1a1a1a', fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: '#fff', fontSize: '1rem' }}>
              EduGestion
            </Typography>
          </Box>

          <List sx={{ flex: 1, pt: 1, px: 0.5 }}>
            {drawerItems.map((item) => (
              <ListItem
                button key={item.text}
                onClick={() => { navigate(item.path); setDrawerOpen(false); }}
                selected={location.pathname === item.path}
                sx={{
                  borderRadius: '8px', mb: 0.25,
                  '&.Mui-selected': { background: 'rgba(15,76,92,0.1)' },
                  '&:hover': { background: 'rgba(15,76,92,0.06)' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: location.pathname === item.path ? '#0f4c5c' : '#4a5568' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItem>
            ))}
          </List>

          <Divider />
          <List sx={{ px: 0.5, pb: 0.5 }}>
            <ListItem button onClick={() => { setDrawerOpen(false); handleLogout(); }}
              sx={{ borderRadius: '8px', '&:hover': { background: 'rgba(239,68,68,0.06)' } }}>
              <ListItemIcon sx={{ minWidth: 36, color: '#ef4444' }}><LogoutIcon /></ListItemIcon>
              <ListItemText primary="Déconnexion" primaryTypographyProps={{ fontSize: '0.875rem', color: '#ef4444' }} />
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* Espace pour compenser l'AppBar fixed */}
      <Toolbar variant="dense" />
    </Box>
  );
};

export default Navbar;
